import { useCallback, useEffect, useMemo, useState } from "react";
import {
    SpreadsheetCellRef,
    SpreadsheetReference,
    SpreadsheetReferenceSource,
    SpreadsheetViewConfig,
    MAX_CELLS_PER_REFERENCE,
} from "@common/schemas/spreadsheetReference.schema";
import { SpreadsheetRefContextValue } from "@components/tasks/SpreadsheetRefContext";
import { useMemoByKeys } from "@hooks/useMemoByKeys";
import {
    buildRangeLabel,
    collectSelectionIndices,
    flattenRows,
} from "./spreadsheetUtils";
import {
    ColumnDef,
    FlatRow,
    SelectionRange,
    SpreadsheetCell,
    SpreadsheetRowData,
} from "./types";

/** Field name used as the label/row-key column in all spreadsheet task bodies. */
const SPREADSHEET_LABEL_FIELD = "label";

type RefCtx = Pick<SpreadsheetRefContextValue, "registerFocusHandler">;

interface UseSpreadsheetSelectionParams {
    /** Task id — selection resets when this changes. */
    taskId: string;
    /** Flattened or tree rows currently rendered. */
    rows: SpreadsheetRowData[] | null;
    /** Column definitions currently rendered. */
    columns: ColumnDef[];
    /** Currently expanded row ids (for flat-row indexing). */
    expandedIds: string[];
    /**
     * Source written into `SpreadsheetReference.source`.
     * Must be one of the values defined in `SpreadsheetReferenceSourceSchema`.
     */
    source: SpreadsheetReferenceSource;
    /** Context from SpreadsheetRefProvider — null when no provider is mounted. */
    refCtx: RefCtx | null;
    /**
     * Optional CSS class to apply to highlighted cells (chip click → focus).
     * Defaults to no class (highlighting disabled) when omitted.
     */
    highlightedCellClassName?: string;
    /**
     * Snapshot of the report view config at the moment selection occurs.
     * Stamped into `pendingReference.viewConfig` so the chip can later
     * compare against the live view to decide whether to highlight or
     * show a "View cells" popover.
     */
    viewConfig?: SpreadsheetViewConfig;
    /**
     * Called whenever `pendingReference` changes. Used to propagate the
     * current selection into a shared context (e.g. SpreadsheetRefContext)
     * so sibling components (e.g. BalanceSheetTaskCommandBar) can read it.
     */
    onPendingReferenceChange?: (ref: SpreadsheetReference | null) => void;
}

interface UseSpreadsheetSelectionResult {
    /** Columns with `className` injected for chip-click highlights. */
    columnsWithHighlight: ColumnDef[];
    /** Call this from `SpreadsheetGrid.onSelectionRangeChange`. */
    handleSelectionRangeChange: (
        cell: SpreadsheetCell | null,
        range: SelectionRange,
    ) => void;
    /**
     * Pending `SpreadsheetReference` derived from the current selection, or
     * `null` when nothing selectable is selected. Wire to a button that adds
     * it to the chat with `useAddCellsToChat`.
     */
    pendingReference: SpreadsheetReference | null;
    /** Cell count of `pendingReference` (0 when no pending ref). */
    pendingCellCount: number;
}

interface SelectionState {
    anchor: SpreadsheetCell | null;
    range: SelectionRange;
}

const EMPTY_SELECTION: SelectionState = {
    anchor: null,
    range: { start: null, end: null, selecting: false },
};

/**
 * Generic spreadsheet-selection hook shared between task bodies that support
 * the "Ask about cells" feature.
 *
 * Handles:
 * - Grid selection tracking + reset on task change
 * - Mapping grid indices to semantic `SpreadsheetCellRef` values
 * - Registering a focus-handler so MessageBubble chips can highlight cells
 * - Building `columnsWithHighlight` for the chip-click highlight overlay
 */
export function useSpreadsheetSelection({
    taskId,
    rows,
    columns,
    expandedIds,
    source,
    refCtx,
    highlightedCellClassName,
    viewConfig,
    onPendingReferenceChange,
}: UseSpreadsheetSelectionParams): UseSpreadsheetSelectionResult {
    const [selection, setSelection] = useState<SelectionState>(EMPTY_SELECTION);

    // Reset on task change so selection from a previous task can't leak.
    useEffect(() => {
        setSelection(EMPTY_SELECTION);
    }, [taskId]);

    // JSON.stringify gives a stable key when content is unchanged but ref changed.
    const expandedIdsKey = JSON.stringify(expandedIds);
    const flatRows = useMemoByKeys<FlatRow[]>(
        () =>
            rows
                ? flattenRows(rows, {
                      expandedIds: new Set(expandedIds),
                      depth: 0,
                      parentId: "",
                  })
                : [],
        [rows, expandedIdsKey],
    );

    // --- Chip-click highlighting ---
    const [highlightedKeys, setHighlightedKeys] = useState<Set<string>>(
        new Set(),
    );

    const registerFocusHandler = refCtx?.registerFocusHandler;
    useEffect(() => {
        if (!registerFocusHandler) {
            return;
        }
        const handler = (refs: SpreadsheetReference[]) => {
            const keys = new Set<string>();
            for (const ref of refs) {
                for (const cell of ref.cells) {
                    keys.add(`${cell.rowKey}__${cell.columnKey}`);
                }
            }
            setHighlightedKeys(keys);
        };
        registerFocusHandler(handler);
        return () => {
            registerFocusHandler(null);
        };
    }, [registerFocusHandler]);

    // --- Cell-ref building ---
    const buildCellRef = useCallback(
        (rowIdx: number, colIdx: number): SpreadsheetCellRef | null => {
            const flatRow = flatRows[rowIdx];
            const col = columns[colIdx];
            if (!flatRow || !col) {
                return null;
            }
            const rowKey = String(flatRow._id);
            const rowLabel = String(flatRow[SPREADSHEET_LABEL_FIELD] ?? rowKey);
            const columnKey = col.field;
            let columnLabel: string;
            if (typeof col.label === "string") {
                columnLabel = col.label;
            } else if (typeof col.labelText === "string") {
                columnLabel = col.labelText;
            } else {
                columnLabel = columnKey;
            }
            const rawValue = flatRow[columnKey];
            const valueSnapshot =
                typeof rawValue === "number" ? rawValue : null;
            return { rowKey, rowLabel, columnKey, columnLabel, valueSnapshot };
        },
        [flatRows, columns],
    );

    const handleSelectionRangeChange = useCallback(
        (cell: SpreadsheetCell | null, range: SelectionRange) => {
            setHighlightedKeys(new Set());
            setSelection({ anchor: cell, range });
        },
        [],
    );

    const pendingReference = useMemo<SpreadsheetReference | null>(() => {
        const { anchor, range } = selection;
        if (!anchor) {
            return null;
        }
        const indices = collectSelectionIndices(anchor, range);
        if (indices.length === 0) {
            return null;
        }
        const cellRefs: SpreadsheetCellRef[] = [];
        for (const { row, col } of indices) {
            const ref = buildCellRef(row, col);
            // Skip the label column — it's not a data cell.
            if (ref && ref.columnKey !== SPREADSHEET_LABEL_FIELD) {
                cellRefs.push(ref);
            }
            if (cellRefs.length >= MAX_CELLS_PER_REFERENCE) {
                break;
            }
        }
        if (cellRefs.length === 0) {
            return null;
        }
        return {
            source,
            taskId,
            rangeLabel: buildRangeLabel(cellRefs),
            cells: cellRefs,
            viewConfig,
        };
    }, [selection, buildCellRef, taskId, source, viewConfig]);

    // Propagate pendingReference to caller (e.g. SpreadsheetRefContext)
    useEffect(() => {
        onPendingReferenceChange?.(pendingReference);
    }, [pendingReference, onPendingReferenceChange]);

    // --- Column highlight overlay ---
    const columnsWithHighlight = useMemo(() => {
        if (!highlightedCellClassName || highlightedKeys.size === 0) {
            return columns;
        }
        return columns.map((col) => ({
            ...col,
            className: (row: Record<string, unknown>) => {
                const base =
                    typeof col.className === "function"
                        ? col.className(row)
                        : (col.className ?? "");
                const key = `${String(row._id ?? "")}__${col.field}`;
                if (highlightedKeys.has(key)) {
                    return `${base} ${highlightedCellClassName}`.trim();
                }
                return base;
            },
        }));
    }, [columns, highlightedKeys, highlightedCellClassName]);

    return {
        columnsWithHighlight,
        handleSelectionRangeChange,
        pendingReference,
        pendingCellCount: pendingReference?.cells.length ?? 0,
    };
}
